/* eslint-disable react-refresh/only-export-components */
import {
  DimeButton,
  DimeCardShellSkeleton,
  DimeDetailPageStates,
  DimeSeparator,
} from '@nudool/design-system';
import { createFileRoute } from '@tanstack/react-router';
import { WarehouseIcon } from 'lucide-react';
import * as React from 'react';

export const Route = createFileRoute('/design-system/detail-page-states')({
  component: DetailPageStatesDocs,
});

function DocsSection({ title, children }: Readonly<{ title: string; children: React.ReactNode }>) {
  return (
    <section className="mb-10">
      <h2 className="mb-1 text-xl font-semibold">{title}</h2>
      <DimeSeparator className="mb-4" />
      {children}
    </section>
  );
}

function Preview({ children }: Readonly<{ children: React.ReactNode }>) {
  return <div className="rounded-lg border border-border bg-background p-6">{children}</div>;
}

const LOAD_ERROR = { message: 'Failed to fetch: NetworkError when attempting to fetch resource.' };
const LOADED = { name: 'Main Warehouse' };

const renderWarehouse = (warehouse: { name: string }) => (
  <p className="text-sm">{warehouse.name}</p>
);

const handleRetry = () => undefined;

const SKELETON = <DimeCardShellSkeleton />;

function DetailPageStatesDocs() {
  return (
    <div>
      <h1 className="mb-1 text-3xl font-bold">Detail Page States</h1>
      <p className="mb-8 text-muted-foreground">
        The loading, load-failure and not-found states of a detail route, in one place. It renders{' '}
        <code>children(data)</code> only once data is present, so the view takes a plain entity prop
        and never repeats the guard.
      </p>

      <DocsSection title="Why">
        <p className="mb-4 text-sm text-muted-foreground">
          These states were hand-rolled per route, and they drifted: across the 16 detail routes
          there were five different loading treatments, and{' '}
          <strong>nine had no error handling at all</strong> — <code>error</code> was never read, so
          a network failure silently rendered &ldquo;Customer not found.&rdquo; with no way to
          retry. Reaching for this component makes that class of bug unreachable rather than merely
          discouraged.
        </p>
        <pre className="overflow-x-auto rounded-md border border-border bg-muted/40 p-4 text-xs">
          {`<DimeDetailPageStates
  data={warehouse}
  loading={loading}
  error={error}
  entityLabel="Warehouse"
  notFoundIcon={<WarehouseIcon />}
  skeleton={<DimeCardShellSkeleton factCount={3} actionCount={3} />}
  onRetry={() => void refetch().catch(() => undefined)}
>
  {(data) => <WarehouseDetailView warehouse={data} />}
</DimeDetailPageStates>`}
        </pre>
      </DocsSection>

      <DocsSection title="Loading">
        <p className="mb-4 text-sm text-muted-foreground">
          <code>skeleton</code> is required, and it should be the page&rsquo;s own shell with
          placeholders in its slots — e.g. <code>DimeCardShellSkeleton</code>, which is built from{' '}
          <code>DimeCardShell</code> itself. A generic skeleton kept in the design system would be a
          second copy of the layout, and it would drift from the real one; this way a header change
          moves the placeholders with it. Guarded on <code>loading &amp;&amp; !data</code>, so a
          background refetch keeps the current page on screen. <code>DimeSpinner</code> stays right
          for indeterminate waits with no layout to reserve, such as an in-flight button.
        </p>
        <Preview>{SKELETON}</Preview>
      </DocsSection>

      <DocsSection title="Load failure">
        <p className="mb-4 text-sm text-muted-foreground">
          Shown when the query errored and no data arrived. It surfaces the real{' '}
          <code>error.message</code> rather than a generic string, and offers a retry when{' '}
          <code>onRetry</code> is given. Because it is guarded on <code>!data</code>, a partial
          error that still returned data renders the page instead of replacing it.
        </p>
        <Preview>
          <DimeDetailPageStates
            data={null}
            loading={false}
            error={LOAD_ERROR}
            entityLabel="Warehouse"
            skeleton={SKELETON}
            onRetry={handleRetry}
          >
            {renderWarehouse}
          </DimeDetailPageStates>
        </Preview>
      </DocsSection>

      <DocsSection title="Not found">
        <p className="mb-4 text-sm text-muted-foreground">
          Shown when the query succeeded but the entity is null — a deleted record or a bad URL.
          Give <code>notFoundAction</code> a route back to the list: a stale link otherwise
          dead-ends on an empty state, with only the breadcrumb to escape it.
        </p>
        <Preview>
          <DimeDetailPageStates
            data={null}
            loading={false}
            entityLabel="Warehouse"
            notFoundIcon={<WarehouseIcon />}
            notFoundAction={<DimeButton variant="outline">Back to warehouses</DimeButton>}
            skeleton={SKELETON}
          >
            {renderWarehouse}
          </DimeDetailPageStates>
        </Preview>
      </DocsSection>

      <DocsSection title="Loaded">
        <p className="mb-4 text-sm text-muted-foreground">
          Once data arrives, <code>children</code> is called with it. The argument is non-null, so
          the view takes a plain entity prop and never repeats the guard.
        </p>
        <Preview>
          <DimeDetailPageStates
            data={LOADED}
            loading={false}
            entityLabel="Warehouse"
            skeleton={SKELETON}
          >
            {renderWarehouse}
          </DimeDetailPageStates>
        </Preview>
      </DocsSection>

      <DocsSection title="Props">
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/50">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Prop</th>
                <th className="px-3 py-2 text-left font-medium">Type</th>
                <th className="px-3 py-2 text-left font-medium">Notes</th>
              </tr>
            </thead>
            <tbody>
              {[
                ['data', 'TData | null | undefined', 'The entity. Null/undefined selects a state.'],
                ['loading', 'boolean', "The query's loading flag."],
                ['error', '{ message } | null', 'Apollo error. Omitting it forfeits the state.'],
                ['entityLabel', 'string', 'Display-cased singular, e.g. "Warehouse".'],
                ['skeleton', 'ReactNode', "Required — the page's own shell, with placeholders."],
                ['notFoundIcon', 'ReactNode', 'Icon for the not-found empty state.'],
                [
                  'notFoundAction',
                  'ReactNode',
                  'Recovery action — usually a link back to the list.',
                ],
                ['onRetry', '() => void', 'Adds a Try again button to the error alert.'],
                ['children', '(data: TData) => ReactNode', 'Called only with non-null data.'],
              ].map(([prop, type, notes]) => (
                <tr key={prop} className="border-b border-border last:border-0">
                  <td className="px-3 py-2 font-mono text-xs">{prop}</td>
                  <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{type}</td>
                  <td className="px-3 py-2 text-muted-foreground">{notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DocsSection>
    </div>
  );
}
